import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { MultiSelect } from "@/components/controls/MultiSelect";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { useUsers } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import { useSaveSubmissionGeneral } from "@/entities/stripeCheckout/hooks";
import { ContractSubmissionSettings } from "@/entities/stripeCheckout/types";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import {
  MAX_ADMINS,
  STRIPE_CHECKOUT_PREFIX,
  generalErrors,
  generalUpdate
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.alerts`;

interface FailureAlertsSectionProps {
  settings: ContractSubmissionSettings | undefined;
  isLoading: boolean;
  subTitle: string;
  isBusy: boolean;
}

// Who hears about a purchase that couldn't become a contract, and the time
// zone dates are read in.
export const FailureAlertsSection: React.FC<FailureAlertsSectionProps> = ({
  settings,
  isLoading,
  subTitle,
  isBusy
}) => {
  const { t } = useTranslation();
  const save = useSaveSubmissionGeneral();
  const userSelect = useUserSelect();
  const [saveError, setSaveError] = useState<string | null>(null);

  const [adminIds, setAdminIds] = useState(settings?.adminUserIds ?? []);
  const [timezone, setTimezone] = useState(settings?.timezone ?? "");
  // Show what was saved once it loads or changes, replacing any older draft.
  const saved = settings
    ? JSON.stringify(
        generalUpdate(settings.adminUserIds, settings.timezone ?? "")
      )
    : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (settings) {
      setAdminIds(settings.adminUserIds);
      setTimezone(settings.timezone ?? "");
    }
  }

  // Chosen admins resolve by id, so they show by name whatever the search shows.
  const selectedAdmins = useUsers(adminIds, {
    enabled: adminIds.length > 0
  });
  const options = useMemo(() => {
    const byId = new Map<string, string>();
    selectedAdmins.users.forEach((user) => {
      if (user?.id) byId.set(user.id, user.displayName);
    });
    userSelect.options.forEach((option) =>
      byId.set(option.id, option.displayName)
    );
    return [...byId].map(([value, label]) => ({ value, label }));
  }, [selectedAdmins.users, userSelect.options]);

  const update = generalUpdate(adminIds, timezone);
  const errors = generalErrors(update);
  const disabled = !settings || isBusy || save.isPending;
  const isDirty = saved !== null && JSON.stringify(update) !== saved;
  const canSave = isDirty && errors.length === 0 && !disabled;

  const handleSave = () => {
    setSaveError(null);
    save.mutate(
      { data: update },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  return (
    <Section
      title={t(`${PREFIX}.title`)}
      subTitle={subTitle}
      isLoading={isLoading}
      items={[
        {
          title: t(`${PREFIX}.admins.title`),
          subTitle: t(`${PREFIX}.admins.subtitle`, { max: MAX_ADMINS }),
          customAction: (
            <div className="w-[320px]" data-test="stripe-checkout-admins">
              <MultiSelect
                options={options}
                selectedValues={adminIds}
                onValueChange={(values) => {
                  setAdminIds(values);
                  setSaveError(null);
                }}
                placeholder={t(`${PREFIX}.admins.placeholder`)}
                isOpen={userSelect.isOpen}
                onOpenChange={userSelect.handleOpenChange}
                showSearch
                onSearch={userSelect.setSearchTerm}
                isLoading={userSelect.isLoading}
                maxCount={3}
                disabled={disabled}
              />
            </div>
          )
        },
        {
          title: t(`${PREFIX}.timezone.title`),
          subTitle: t(`${PREFIX}.timezone.subtitle`),
          customAction: (
            <Input
              data-test="stripe-checkout-timezone"
              aria-label={t(`${PREFIX}.timezone.title`)}
              placeholder={t(`${PREFIX}.timezone.placeholder`)}
              value={timezone}
              onChange={(event) => {
                setTimezone(event.target.value);
                setSaveError(null);
              }}
              disabled={disabled}
              containerClassName="w-[240px]"
            />
          )
        }
      ]}
    >
      {(errors.length > 0 || saveError) && (
        <div
          className="flex flex-col gap-1"
          role="alert"
          data-test="stripe-checkout-alerts-error"
        >
          {errors.map((error) => (
            <Text key={error} variant="smBodyError">
              {t(`${PREFIX}.errors.${error}`, { max: MAX_ADMINS })}
            </Text>
          ))}
          {saveError && <Text variant="smBodyError">{saveError}</Text>}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          data-test="stripe-checkout-save-alerts"
        >
          {t(`${PREFIX}.save`)}
        </Button>
      </div>
    </Section>
  );
};
